/**
 * @file experienceCard.component.tsx
 * @path apps/website/src/features/experiences/components/experienceCard.component.tsx
 */
import { cn } from "@sirbastians/utils";
import { motion } from "framer-motion";
import { Alarm, GeoAltFill } from "react-bootstrap-icons";
import { useNavigate } from "react-router-dom"; // ✨ NEW: Import useNavigate
import { ExperienceSummary } from "../experience.types";
import { getActivityIcon } from "@/data/activities";

interface ExperienceCardProps {
  item: ExperienceSummary;
  index: number;
}

export const ExperienceCard = ({ item, index }: ExperienceCardProps) => {
  const navigate = useNavigate(); // ✨ NEW: Initialize navigate
  const isTilted = index === 1;
  const Icon = item.iconName ? getActivityIcon(item.iconName) : null;

  // ✨ NEW: Click handler that uses your path structure
  const handleCardClick = () => {
    navigate(`/experiences/${item.slug}`);
  };

  return (
    <motion.div
      onClick={handleCardClick} // ✨ NEW: Attach click event
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0, rotateZ: isTilted ? -2 : 0 }}
      whileHover={{ y: -10, rotateZ: 0, scale: 1.02, zIndex: 10 }}
      transition={{ type: "spring", stiffness: 300, damping: 20 }}
      className={cn(
        "group relative flex flex-col h-[520px] p-6 rounded-lg",
        "bg-card text-card-foreground border border-border transition-all duration-500",
        "cursor-pointer",
      )}
    >
      <div className="flex justify-between items-center z-30 mb-4">
        <div className="flex gap-2">
          <span className="text-[9px] uppercase tracking-[0.2em] text-accent bg-accent/10 px-2 py-1 rounded-sm border border-accent/20">
            {item.category}
          </span>
          {item.status !== "active" && (
            <span className="text-[9px] uppercase tracking-[0.2em] text-muted-foreground bg-secondary px-2 py-1 rounded-sm">
              {item.status}
            </span>
          )}
        </div>
        <div className="flex flex-col items-end">
          <span className="text-[9px] uppercase text-muted-foreground tracking-[0.3em] mb-1">
            Rate
          </span>
          <div className="font-bold text-xl text-foreground">
            {item.price.amount === 0 ? (
              "Complimentary"
            ) : (
              <>
                {item.price.isFromPrice && (
                  <span className="text-[10px] font-normal text-muted-foreground mr-1">
                    from
                  </span>
                )}
                <span className="text-sm font-medium mr-0.5">
                  {item.price.currency === "ZAR" ? "R" : "$"}
                </span>
                {item.price.amount.toLocaleString()}
              </>
            )}
          </div>
        </div>
      </div>

      <div className="relative flex-1 flex items-center justify-center overflow-hidden mb-6">
        <div className="absolute inset-0 bg-foreground/5 rounded-full blur-[100px] group-hover:bg-foreground/10 transition-colors pointer-events-none" />
        <img
          src={item.image}
          alt={item.title}
          className="w-full h-full object-cover relative z-10 transition-transform duration-700 group-hover:scale-110"
        />
      </div>

      <div className="space-y-4">
        <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-[10px] text-muted-foreground font-open-sans uppercase tracking-wider">
          {item.duration && (
            <span className="flex items-center gap-1.5">
              <Alarm className="h-3 w-3 text-accent" /> {item.duration}
            </span>
          )}
          {item.location && (
            <span className="flex items-center gap-1.5 truncate max-w-[150px]">
              <GeoAltFill className="h-3 w-3 text-accent" /> {item.location}
            </span>
          )}
          {Icon && item.difficulty && (
            <span className="flex items-center gap-1.5">
              <Icon className="h-3 w-3 text-accent" /> {item.difficulty}
            </span>
          )}
        </div>

        <div className="space-y-2">
          <h4 className="font-bold text-lg leading-tight group-hover:text-accent transition-colors">
            {item.title}
          </h4>
          <p className="font-open-sans text-muted-foreground text-sm line-clamp-2 leading-relaxed font-light">
            {item.shortDescription}
          </p>
        </div>

        <div className="pt-4 border-t border-border flex justify-center items-end">
          <div className="text-xs uppercase tracking-widest text-accent underline-offset-4 group-hover:underline transition-opacity duration-300">
            View Details →
          </div>
        </div>
      </div>
    </motion.div>
  );
};
